import React, { useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Clock, CheckCircle2, XCircle, Loader2, Hourglass, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch { return iso; }
};

/**
 * Admin grace-period queue. Lives as a tab on the Students page so admins
 * stay in one workflow when they're triaging student timelines.
 */
export default function ExtensionRequestsTab({ rows, loading, onRefresh }) {
    const [decision, setDecision] = useState(null); // { req, mode: "approve"|"deny" }
    const [days, setDays] = useState(0);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const open = (req, mode) => {
        setDecision({ req, mode });
        setDays(req.requested_days || 14);
        setNote("");
    };
    const close = () => {
        setDecision(null);
        setDays(0);
        setNote("");
    };

    const submit = async () => {
        if (!decision) return;
        setSaving(true);
        try {
            if (decision.mode === "approve") {
                const n = Number(days);
                if (!n || n < 1 || n > 365) {
                    toast.error("Approved days must be between 1 and 365.");
                    setSaving(false);
                    return;
                }
                await api.post(
                    `/admin/extension-requests/${decision.req.id}/approve`,
                    { approved_days: n, decision_note: note.trim() || undefined },
                );
                toast.success(`Approved ${n} day${n === 1 ? "" : "s"}.`);
            } else {
                await api.post(
                    `/admin/extension-requests/${decision.req.id}/deny`,
                    { decision_note: note.trim() || undefined },
                );
                toast.success("Request denied.");
            }
            close();
            onRefresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save decision.");
        } finally {
            setSaving(false);
        }
    };

    const pending = rows.filter((r) => r.status === "pending");
    const decided = rows.filter((r) => r.status !== "pending").slice(0, 30);

    return (
        <>
            <Card className="surface-card border-border" data-testid="extension-requests-card">
                <CardContent className="p-0">
                    <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold flex items-center gap-2">
                                <Hourglass className="h-3.5 w-3.5" />
                                Pending requests
                            </div>
                            <h3 className="font-display text-lg text-primary mt-0.5">
                                {pending.length === 0
                                    ? "Nothing waiting on you"
                                    : `${pending.length} student${pending.length === 1 ? "" : "s"} waiting`}
                            </h3>
                        </div>
                    </div>
                    <Table className="data-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Student</TableHead>
                                <TableHead>Program</TableHead>
                                <TableHead className="w-32">Window ends</TableHead>
                                <TableHead className="w-28">Requested</TableHead>
                                <TableHead>Reason</TableHead>
                                <TableHead className="w-56 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            ) : pending.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="py-12 text-center">
                                        <div className="empty-state !py-6">
                                            <div className="empty-icon-wrap">
                                                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                                            </div>
                                            <p className="font-display text-lg text-foreground">
                                                Inbox zero
                                            </p>
                                            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                                                No pending grace-period requests right now.
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                pending.map((r) => (
                                    <TableRow key={r.id} data-testid={`extension-pending-${r.id}`}>
                                        <TableCell>
                                            <div className="font-medium text-foreground truncate">
                                                {r.student_name || "—"}
                                            </div>
                                            <div className="text-xs text-muted-foreground truncate">
                                                {r.student_id ? (
                                                    <span className="font-mono">
                                                        {r.student_id} ·{" "}
                                                    </span>
                                                ) : null}
                                                {r.student_email}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-foreground/85 truncate">
                                            {r.program_title}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {fmtDate(r.current_end_date)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="border-border">
                                                {r.requested_days}d
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground max-w-[20rem]">
                                            {r.reason ? (
                                                <span className="line-clamp-2 italic">
                                                    “{r.reason}”
                                                </span>
                                            ) : (
                                                <span className="opacity-60">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="inline-flex items-center gap-1.5">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-emerald-500/50 text-emerald-700 hover:bg-emerald-500/10 gap-1.5"
                                                    onClick={() => open(r, "approve")}
                                                    data-testid={`extension-approve-${r.id}`}
                                                >
                                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                                    Approve
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-rose-500/40 text-rose-700 hover:bg-rose-500/10 gap-1.5"
                                                    onClick={() => open(r, "deny")}
                                                    data-testid={`extension-deny-${r.id}`}
                                                >
                                                    <XCircle className="h-3.5 w-3.5" />
                                                    Deny
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {decided.length > 0 && (
                <Card
                    className="surface-card border-border mt-5"
                    data-testid="extension-history-card"
                >
                    <CardContent className="p-0">
                        <div className="px-6 py-4 border-b border-border">
                            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold flex items-center gap-2">
                                <MessageSquare className="h-3.5 w-3.5" />
                                Recent decisions
                            </div>
                        </div>
                        <Table className="data-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Student</TableHead>
                                    <TableHead>Program</TableHead>
                                    <TableHead className="w-32">Decided</TableHead>
                                    <TableHead className="w-44">Outcome</TableHead>
                                    <TableHead>Note</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {decided.map((r) => (
                                    <TableRow key={r.id} data-testid={`extension-decided-${r.id}`}>
                                        <TableCell>
                                            <div className="font-medium text-foreground truncate">
                                                {r.student_name || "—"}
                                            </div>
                                            <div className="text-xs text-muted-foreground truncate">
                                                {r.student_email}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-foreground/85 truncate">
                                            {r.program_title}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {fmtDate(r.decided_at)}
                                        </TableCell>
                                        <TableCell>
                                            {r.status === "approved" ? (
                                                <Badge className="bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 gap-1">
                                                    <CheckCircle2 className="h-3 w-3" />
                                                    +{r.approved_days}d ·{" "}
                                                    {fmtDate(r.new_end_date)}
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-rose-500/15 text-rose-700 border border-rose-500/30 gap-1">
                                                    <XCircle className="h-3 w-3" />
                                                    Denied
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground italic max-w-[24rem] truncate">
                                            {r.decision_note || "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            <Dialog open={!!decision} onOpenChange={(o) => !o && close()}>
                <DialogContent
                    className="max-w-md"
                    data-testid="extension-decision-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-primary">
                            {decision?.mode === "approve"
                                ? "Approve grace period"
                                : "Deny request"}
                        </DialogTitle>
                        <DialogDescription>
                            {decision?.req?.student_name} ·{" "}
                            {decision?.req?.program_title} · requested{" "}
                            {decision?.req?.requested_days} days
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-2">
                        {decision?.mode === "approve" && (
                            <div>
                                <Label className="text-sm font-semibold">
                                    Approved days
                                </Label>
                                <div className="flex gap-2 mt-1.5">
                                    {[7, 14, 30, 60].map((d) => (
                                        <button
                                            type="button"
                                            key={d}
                                            onClick={() => setDays(d)}
                                            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                                                Number(days) === d
                                                    ? "bg-primary text-primary-foreground border-primary"
                                                    : "bg-background border-border hover:bg-muted"
                                            }`}
                                        >
                                            {d}d
                                        </button>
                                    ))}
                                    <Input
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={days}
                                        onChange={(e) => setDays(e.target.value)}
                                        className="w-24"
                                        data-testid="extension-approve-days-input"
                                    />
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    The new end date is calculated from today
                                    if their previous window has already
                                    closed; otherwise from their current end date.
                                </p>
                            </div>
                        )}
                        <div>
                            <Label className="text-sm font-semibold">
                                Note for the student{" "}
                                <span className="font-normal text-muted-foreground">(optional)</span>
                            </Label>
                            <Textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={3}
                                placeholder={
                                    decision?.mode === "approve"
                                        ? "e.g. 'Granted because of medical leave.'"
                                        : "e.g. 'Please re-enrol next term.'"
                                }
                                data-testid="extension-decision-note"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={close}>
                            Cancel
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={saving}
                            className={
                                decision?.mode === "deny"
                                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    : "bg-primary hover:bg-primary/90"
                            }
                            data-testid="extension-decision-confirm"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    Saving…
                                </>
                            ) : decision?.mode === "approve" ? (
                                "Approve"
                            ) : (
                                "Deny"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
